<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import NextButton from 'dashboard/components-next/button/Button.vue';

// `compact` renders the control as a chip for the reply box's top row (used
// while the agent can type); the default is the card laid over the locked
// editor while the AI answers.
defineProps({
  compact: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();
const { t } = useI18n();
const currentChat = useMapGetter('getSelectedChat');

// Duration options for pausing the AI. `minutes: null` means pause until the
// agent manually turns it back on ("forever").
const AI_PAUSE_OPTIONS = [
  { key: 'FIVE_MIN', minutes: 5 },
  { key: 'FIFTEEN_MIN', minutes: 15 },
  { key: 'THIRTY_MIN', minutes: 30 },
  { key: 'ONE_HOUR', minutes: 60 },
  { key: 'FOUR_HOURS', minutes: 240 },
  { key: 'FOREVER', minutes: null },
];

const isMenuOpen = ref(false);

const pausedUntil = computed(
  () => currentChat.value?.custom_attributes?.ai_paused_until
);

// "pending" is the status under which the Captain bot actively replies.
const isPending = computed(() => currentChat.value?.status === 'pending');

const isPaused = computed(() => {
  const value = pausedUntil.value;
  if (!value) return false;
  if (value === 'forever') return true;
  return new Date(value).getTime() > Date.now();
});

const pausedUntilLabel = computed(() => {
  const value = pausedUntil.value;
  if (value === 'forever') {
    return t('CONVERSATION.AI_CONTROL.PAUSED_INDEFINITELY');
  }
  const time = new Date(value).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
  return t('CONVERSATION.AI_CONTROL.PAUSED_UNTIL', { time });
});

const updatePause = value => {
  return store.dispatch('updateCustomAttributes', {
    conversationId: currentChat.value.id,
    customAttributes: {
      ...(currentChat.value.custom_attributes || {}),
      ai_paused_until: value,
    },
  });
};

const pauseFor = option => {
  isMenuOpen.value = false;
  const value = option.minutes
    ? new Date(Date.now() + option.minutes * 60000).toISOString()
    : 'forever';
  updatePause(value);
};

const resumeAi = () => updatePause(null);
</script>

<template>
  <div
    class="flex items-center gap-2 text-sm min-w-0"
    :class="
      compact
        ? 'px-2 py-1 rounded-full bg-n-alpha-2'
        : 'px-3.5 py-2 rounded-2xl shadow-md border border-n-weak bg-n-solid-2'
    "
  >
    <span class="i-lucide-sparkles size-4 text-n-brand flex-shrink-0" />
    <template v-if="!isPaused">
      <span
        class="flex-1 min-w-0 truncate"
        :class="compact ? 'text-n-slate-11 text-xs' : 'text-n-slate-12'"
      >
        {{
          isPending
            ? t('CONVERSATION.AI_CONTROL.AI_HANDLING')
            : t('CONVERSATION.AI_CONTROL.AI_ON')
        }}
      </span>
      <div class="relative flex-shrink-0">
        <NextButton
          :sm="!compact"
          :xs="compact"
          slate
          faded
          :label="t('CONVERSATION.AI_CONTROL.TURN_OFF')"
          trailing-icon
          icon="i-lucide-chevron-down"
          @click="isMenuOpen = !isMenuOpen"
        />
        <div
          v-if="isMenuOpen"
          v-on-click-outside="() => (isMenuOpen = false)"
          class="absolute z-50 p-1 mb-2 border shadow-lg bottom-full ltr:right-0 rtl:left-0 min-w-52 bg-n-solid-2 rounded-xl border-n-weak"
        >
          <div
            class="px-2 py-1 uppercase text-xxs tracking-wider text-n-slate-10"
          >
            {{ t('CONVERSATION.AI_CONTROL.TURN_OFF_FOR') }}
          </div>
          <button
            v-for="option in AI_PAUSE_OPTIONS"
            :key="option.key"
            class="flex items-center w-full px-2 py-1.5 text-sm text-left rounded-lg rtl:text-right text-n-slate-12 hover:bg-n-alpha-2"
            @click="pauseFor(option)"
          >
            {{ t(`CONVERSATION.AI_CONTROL.DURATIONS.${option.key}`) }}
          </button>
        </div>
      </div>
    </template>
    <template v-else>
      <span
        class="flex-1 min-w-0 truncate text-n-slate-11"
        :class="{ 'text-xs': compact }"
      >
        {{ pausedUntilLabel }}
      </span>
      <NextButton
        :sm="!compact"
        :xs="compact"
        teal
        faded
        class="flex-shrink-0"
        :label="t('CONVERSATION.AI_CONTROL.TURN_ON')"
        icon="i-lucide-sparkles"
        @click="resumeAi"
      />
    </template>
  </div>
</template>
